import { type DefaultMarketAccounts, EMPTY_ARRAY, EMPTY_OBJECT, useDefaultMarketAccounts } from '@talos/kyoko';
import { compact, get } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import type {
  MarketSelectorAPI,
  MarketSelectorCustomPresetItem,
  MarketSelectorEnrichmentSpec,
  MarketSelectorItem,
  MarketSelectorPreset,
  MarketSelectorValue,
} from './types';
import { isMarketSelectorItemSelectable } from './utils/items';

export interface UseMarketSelectorParams {
  value: MarketSelectorValue;

  /** List of available presets to select from. Only required if using presets functionality. */
  presets?: MarketSelectorPreset[];

  /** List of custom items, if any, to show in the Presets list. Will be used in addition to the default "Custom" item */
  customPresetItems?: MarketSelectorCustomPresetItem[];

  /** A list of enrichments to apply to the Market Selector */
  enrichmentSpecs?: MarketSelectorEnrichmentSpec<any>[];

  /** A list of selectable items */
  items: MarketSelectorItem[] | undefined;

  /** The main onChange handler. Will emit the entire new selection state on every change: both markets and accounts */
  onChange: (newValue: MarketSelectorValue) => void;

  options?: {
    /** When true, will allow you to select multiple accounts per market. However only accounts within one credential per market. */
    allowMultipleAcctsPerCred?: boolean;
  };
}

export const useMarketSelector = ({
  value,
  items: inputItems,
  presets: inputPresets,
  enrichmentSpecs,
  customPresetItems = EMPTY_ARRAY,
  onChange,
  options = EMPTY_OBJECT,
}: UseMarketSelectorParams): MarketSelectorAPI => {
  const allowMultipleAcctsPerCred = options.allowMultipleAcctsPerCred ?? false;
  const { defaultMarketAccounts } = useDefaultMarketAccounts();

  const items = inputItems ?? EMPTY_ARRAY;
  const itemsMap = useMemo(() => new Map(items.map(option => [option.name, option])), [items]);
  const itemsByMarket: Map<string, MarketSelectorItem[]> = useMemo(() => {
    const newMap = new Map<string, MarketSelectorItem[]>();
    for (const item of items) {
      newMap.set(item.market.Name, [...(newMap.get(item.market.Name) ?? []), item]);
    }
    return newMap;
  }, [items]);

  const presets = useMemo(() => {
    if (!inputPresets) {
      return undefined;
    }

    // We only show / allow the implementer to interact with presets which are represented by at least one item somewhere.
    return inputPresets.filter(preset => {
      return preset.itemIDs.some(itemId => itemsMap.has(itemId));
    });
  }, [inputPresets, itemsMap]);

  const presetsByID = useMemo(() => {
    if (!presets) {
      return undefined;
    }

    return new Map(presets.map(preset => [preset.id, preset]));
  }, [presets]);

  const getItemsInPreset = useCallback(
    (presetID: string) => {
      return getItemsFromPreset(presetID, presetsByID, itemsMap);
    },
    [presetsByID, itemsMap]
  );

  const selections = value.selections;

  const selectionsSet = useMemo(() => new Set(selections), [selections]);

  const select = useCallback(
    (itemOrKey: MarketSelectorItem | string) => {
      const key = typeof itemOrKey === 'string' ? itemOrKey : itemOrKey.name;
      const validatedNewSelections = validateNewSelections([key], itemsMap, selections, allowMultipleAcctsPerCred);
      onChange({ type: 'custom', selections: validatedNewSelections });
    },
    [onChange, itemsMap, selections, allowMultipleAcctsPerCred]
  );

  const unselect = useCallback(
    (item: MarketSelectorItem | string) => {
      const key = typeof item === 'string' ? item : item.name;
      onChange({ type: 'custom', selections: selections.filter(s => s !== key) });
    },
    [onChange, selections]
  );

  const setSelected = useCallback(
    (item: MarketSelectorItem | string, selected: boolean) => {
      selected ? select(item) : unselect(item);
    },
    [select, unselect]
  );

  const selectMany = useCallback(
    (items: (MarketSelectorItem | string)[]) => {
      const newSelectionKeys = items.map(item => (typeof item === 'string' ? item : item.name));
      const validatedNewSelections = validateNewSelections(
        newSelectionKeys,
        itemsMap,
        selections,
        allowMultipleAcctsPerCred
      );
      return onChange({ type: 'custom', selections: validatedNewSelections });
    },
    [onChange, itemsMap, selections, allowMultipleAcctsPerCred]
  );

  const unselectMany = useCallback(
    (items: (MarketSelectorItem | string)[]) => {
      const keys = items.map(item => (typeof item === 'string' ? item : item.name));
      const keysSet = new Set(keys);
      onChange({ type: 'custom', selections: selections.filter(selection => !keysSet.has(selection)) });
    },
    [onChange, selections]
  );

  const selectOnly = useCallback(
    (item: MarketSelectorItem | string) => {
      onChange({ type: 'custom', selections: [typeof item === 'string' ? item : item.name] });
    },
    [onChange]
  );

  const selectAll = useCallback(
    (subset?: MarketSelectorItem[]) => {
      const itemsToActOn = subset ?? items;

      const marketsFound = new Set<string>();
      const marketsSelected = new Set<string>();
      for (const item of itemsToActOn) {
        marketsFound.add(item.market.Name);
        if (selectionsSet.has(item.name)) {
          marketsSelected.add(item.market.Name);
        }
      }

      // this marketsToAdd data set is all the markets which do not have some selection made within them already
      // So for all these markets in this array, we must make one selection for each.
      const marketsToAdd = [...marketsFound].filter(market => !marketsSelected.has(market));

      const newSelections = compact(
        marketsToAdd.map(market => {
          // we need to decide what item to select within this market.
          const itemsWithinMarket = itemsByMarket.get(market);
          if (!itemsWithinMarket) {
            return undefined;
          }

          // If we can find some available item, we select that one
          const firstSelectableItem = getItemToSelectInMarket(market, itemsWithinMarket, defaultMarketAccounts);
          if (firstSelectableItem) {
            return firstSelectableItem.name;
          }

          // If we didn't find any selectable items, then don't select anything within this market.
          return undefined;
        })
      );

      onChange({ type: 'custom', selections: [...selections, ...newSelections] });
    },
    [items, itemsByMarket, onChange, selections, selectionsSet, defaultMarketAccounts]
  );

  const unselectAll = useCallback(
    (subset?: MarketSelectorItem[]) => {
      const selectionsInProvidedSubset = subset
        ? subset.filter(item => selectionsSet.has(item.name)).map(selectedItem => selectedItem.name)
        : undefined;

      unselectMany(selectionsInProvidedSubset ?? selections);
    },
    [selections, selectionsSet, unselectMany]
  );

  const selectMarket = useCallback(
    (marketName: string, selected: boolean) => {
      const itemsWithinMarket = itemsByMarket.get(marketName);
      if (!itemsWithinMarket) {
        return;
      }

      const itemsWithinMarketSet = new Set(itemsWithinMarket.map(i => i.name));
      // Start by unselecting all items within the market
      const newSelections = selections.filter(s => !itemsWithinMarketSet.has(s));

      if (selected) {
        const firstSelectableItem = getItemToSelectInMarket(marketName, itemsWithinMarket, defaultMarketAccounts);
        if (firstSelectableItem) {
          newSelections.push(firstSelectableItem.name);
        }
      }

      onChange({ type: 'custom', selections: newSelections });
    },
    [itemsByMarket, onChange, selections, defaultMarketAccounts]
  );

  const selectPreset = useCallback(
    (newPresetID: string) => {
      const presetItems = getItemsInPreset(newPresetID);
      const selectablePresetItems = presetItems.filter(isMarketSelectorItemSelectable);

      onChange({ type: 'preset', presetID: newPresetID, selections: selectablePresetItems.map(item => item.name) });
    },
    [onChange, getItemsInPreset]
  );

  const allPossibleSelectionsMade = useMemo(() => {
    // calculate if all possible selections are made
    // We gather both the sets of selectable and selected markets, and then compare them.
    const selectableMarkets: string[] = [];
    itemsByMarket.forEach((itemsInMarket, marketName) => {
      const selectable = itemsInMarket.some(item => isMarketSelectorItemSelectable(item));
      if (selectable) {
        selectableMarkets.push(marketName);
      }
    });

    const selectedMarkets = new Set<string>();
    selections.forEach(selection => {
      const selectedItem = itemsMap.get(selection);
      if (selectedItem) {
        selectedMarkets.add(selectedItem.market.Name);
      }
    });

    return !selectableMarkets.some(m => !selectedMarkets.has(m));
  }, [itemsByMarket, itemsMap, selections]);

  const hasMultipleSelectionsPerMarket = useMemo(() => {
    if (!selections || !itemsMap) {
      return false;
    }

    return containsMultipleSelectionsPerMarket(selections, itemsMap);
  }, [selections, itemsMap]);

  return useMemo(
    () => ({
      ready: inputItems != null,
      presetsReady: presets != null,

      preset: value.type === 'preset' ? value.presetID : undefined,
      presets,
      presetsByID,
      getItemsInPreset,

      enrichments: enrichmentSpecs ?? EMPTY_ARRAY,

      selections,
      selectionsSet,
      allPossibleSelectionsMade,
      hasMultipleSelectionsPerMarket,
      items,
      itemsMap,
      itemsByMarket,

      select,
      unselect,
      selectMany,
      unselectMany,
      selectOnly,
      selectAll,
      unselectAll,
      setSelected,
      selectMarket,

      selectPreset,

      customPresetItems,
    }),
    [
      inputItems,
      value,

      presets,
      presetsByID,
      getItemsInPreset,

      enrichmentSpecs,

      selections,
      selectionsSet,
      allPossibleSelectionsMade,
      hasMultipleSelectionsPerMarket,
      items,
      itemsMap,
      itemsByMarket,

      select,
      unselect,
      selectMany,
      selectOnly,
      unselectMany,
      selectAll,
      unselectAll,
      setSelected,
      selectMarket,

      selectPreset,

      customPresetItems,
    ]
  );
};

/** Extract all items in a preset */
export function getItemsFromPreset(
  presetID: string,
  presetsByID: Map<string, MarketSelectorPreset> | undefined,
  itemsMap: Map<string, MarketSelectorItem>
): MarketSelectorItem[] {
  return compact(presetsByID?.get(presetID)?.itemIDs.map(id => itemsMap.get(id)));
}

/**
 * Given a new array of selections coming in, this function ensures that we end up with a valid selection array.
 *
 * If allow is true, it will make sure that only one credential per market is selected, and that only one cred per market is selected.
 * If it is false, it'll just check that there is only one selection per market.
 *
 * Returns an absolute selection list: a validated combination of the previous selections and the new selections.
 */
function validateNewSelections(
  newSelections: string[],
  itemsMap: Map<string, MarketSelectorItem>,
  prevSelections: string[],
  allowMultipleAcctsPerCred: boolean
) {
  if (allowMultipleAcctsPerCred) {
    const prevSelectionsToRemove = new Set<string>();
    // You can have only one credential selected per market
    const newSelectionsByMarketByCredential = buildSelectionsByMarketByCredentialMap(newSelections, itemsMap);
    const prevSelectionsByMarketByCredential = buildSelectionsByMarketByCredentialMap(prevSelections, itemsMap);

    // FYI we assume here that newSelections and prevSelections are valid separately. What we are doing
    // in this function is removing any selections from prev that become invalid after combining with new.
    // --
    // Approach is... go through each new market + cred, and see if there is any diff market+cred selection in prev.
    // If there is in prev, remove the selections made within that credential.
    newSelectionsByMarketByCredential.forEach((newCredMap, marketName) => {
      const prevCredMap = prevSelectionsByMarketByCredential.get(marketName);
      const prevSelectedCred = prevCredMap ? [...prevCredMap.entries()].at(0) : undefined;
      if (prevSelectedCred) {
        const [prevSelectedCredID, prevSelectedCredSelections] = prevSelectedCred;
        if (!newCredMap.has(prevSelectedCredID)) {
          prevSelectedCredSelections.forEach(s => prevSelectionsToRemove.add(s));
        }
      }
    });

    const stillValidPreviousSelections = [...prevSelections].filter(s => !prevSelectionsToRemove.has(s));
    return [...stillValidPreviousSelections, ...newSelections];
  } else {
    // Base case, you can only have one selection per market
    const newSelectionsMarketsSet = new Set(compact(newSelections.map(key => itemsMap.get(key)?.market.Name)));
    const stillValidPreviousSelections = prevSelections.filter(selection => {
      const selectionItem = itemsMap.get(selection)?.market.Name;
      if (!selectionItem) {
        // shouldn't happen but if it does then regard as not valid and remove selection
        return false;
      }

      return !newSelectionsMarketsSet.has(selectionItem);
    });

    return [...stillValidPreviousSelections, ...newSelections];
  }
}

function buildSelectionsByMarketByCredentialMap(
  selections: string[],
  itemsMap: Map<string, MarketSelectorItem>
): Map<string, Map<number | null, Set<string>>> {
  const selectionsByMarketByCredential = new Map<string, Map<number | null, Set<string>>>();

  for (const selection of selections) {
    const item = itemsMap.get(selection);
    if (!item) {
      continue;
    }

    const credentialsMap =
      selectionsByMarketByCredential.get(item.market.Name) ?? new Map<number | null, Set<string>>();
    // For market accounts, use the CredID of course. If the selection is a market (marketAccount nullish), we model that as itemCredential = null.
    const itemCredential = item.marketAccount ? item.marketAccount.CredentialID : null;
    const selectionsSetForCredential = credentialsMap.get(itemCredential) ?? new Set();
    selectionsSetForCredential.add(selection);
    credentialsMap.set(itemCredential, selectionsSetForCredential);
    selectionsByMarketByCredential.set(item.market.Name, credentialsMap);
  }

  return selectionsByMarketByCredential;
}

/**
 * Figures out which item to select assuming that we are to select one item within this provided array of items.
 *
 * If a configuration of default market accounts is provided, the function will attempt to select a default account first,
 * and if none is found, fallback to selecting the first selectable account alphabetically.
 *
 * If none of these items are selectable, then the function returns undefined.
 */
function getItemToSelectInMarket(
  marketName: string,
  itemsInMarket: MarketSelectorItem[],
  defaultMarketAccounts?: DefaultMarketAccounts
): MarketSelectorItem | undefined {
  // Try to use the default account within the market, if any.
  const defaultAccount = get(defaultMarketAccounts, marketName);
  if (defaultAccount) {
    // If there exists a default account, see if that account is one of our selectable accounts. If it is, select it.
    const defaultAccountItem = itemsInMarket.find(
      item => item.marketAccount?.Name === defaultAccount && isMarketSelectorItemSelectable(item)
    );
    if (defaultAccountItem) {
      return defaultAccountItem;
    }
  }

  // If no default account was found, just take the first one alphabetically.
  return itemsInMarket
    .toSorted((a, b) => (a.displayName ?? a.name).localeCompare(b.displayName ?? b.name))
    .find(isMarketSelectorItemSelectable);
}

/** This function returns what the selection would be of a certain preset given the set of preset and items available */
export function getSelectionsFromPreset(
  preset: MarketSelectorPreset | undefined,
  itemsMap: Map<string, MarketSelectorItem>
): MarketSelectorItem[] {
  if (!preset) {
    return [];
  }

  const selections: MarketSelectorItem[] = [];
  const presetItemIDs = preset.itemIDs ?? [];
  for (const itemID of presetItemIDs) {
    const item = itemsMap.get(itemID);
    if (item != null && isMarketSelectorItemSelectable(item)) {
      selections.push(item);
    }
  }

  return selections;
}

export function containsMultipleSelectionsPerMarket(selections: string[], itemsMap: Map<string, MarketSelectorItem>) {
  const foundMarkets = new Set<string>();
  for (const selection of selections) {
    const item = itemsMap.get(selection);
    if (!item) {
      continue;
    }
    if (foundMarkets.has(item.market.Name)) {
      return true;
    }
    foundMarkets.add(item.market.Name);
  }
  return false;
}
